import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { getMachineHealthAlerts } from '../../helper/MachineHealthHelper';
import { getHealthScoreMeta, normalizeAlertSeverity } from '../../utils/machinePayloadUtils';
import { TIMELINE_CONST } from '../../common/constant/common-constants';
import './AlertArchiveModal.css';

const ARCHIVE_DAYS = 7;

const AlertArchiveModal = ({ isOpen, onClose, machineUDI, board = 'iomt' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [alerts, setAlerts] = useState([]);

  const fetchArchive = useCallback(() => {
    if (!machineUDI) return;
    setLoading(true);
    setError('');

    const from = new Date(Date.now() - ARCHIVE_DAYS * 24 * 60 * 60 * 1000).toISOString();
    getMachineHealthAlerts(machineUDI, { from, board })
      .then((data) => {
        setAlerts(Array.isArray(data?.alerts) ? data.alerts : []);
      })
      .catch((err) => {
        setError(err?.message || 'Unable to load alert archive.');
        setAlerts([]);
      })
      .finally(() => setLoading(false));
  }, [machineUDI, board]);

  useEffect(() => {
    if (isOpen) {
      fetchArchive();
    }
  }, [isOpen, fetchArchive]);

  if (!isOpen) return null;

  const boardLabel = board === 'som' ? 'SOM' : 'IOMT';

  return (
    <div className="mh-archive-modal" role="dialog" aria-modal="true" aria-labelledby="mh-archive-title">
      <button type="button" className="mh-archive-backdrop" onClick={onClose} aria-label="Close archive" />
      <div className="mh-archive-panel">
        <header className="mh-archive-header">
          <div>
            <p className="mh-archive-eyebrow">{boardLabel} Alert Archive</p>
            <h2 id="mh-archive-title">Last {ARCHIVE_DAYS} days</h2>
          </div>
          <button type="button" className="mh-archive-close" onClick={onClose}>×</button>
        </header>

        {loading && (
          <div className="mh-archive-state">
            <div className="mh-spinner" />
            <p>Loading archived alerts…</p>
          </div>
        )}

        {!loading && error && (
          <div className="mh-archive-state mh-archive-state--error">
            <p>{error}</p>
            <button type="button" className="mh-archive-retry" onClick={fetchArchive}>Retry</button>
          </div>
        )}

        {!loading && !error && alerts.length === 0 && (
          <div className="mh-archive-state">
            <p>No archived alerts in the selected period.</p>
          </div>
        )}

        {!loading && !error && alerts.length > 0 && (
          <div className="mh-archive-list">
            {alerts.map((alert) => {
              const scoreMeta = getHealthScoreMeta(alert.healthScore);
              const severityTone = normalizeAlertSeverity(alert.severity);
              return (
                <article
                  key={`${alert.recordedAt}-${alert.code}-${alert.detail}`}
                  className={`mh-archive-item mh-archive-item--${severityTone}`}
                >
                  <div className="mh-archive-item-head">
                    <strong>{alert.code}</strong>
                    <span className="mh-archive-time">
                      {alert.recordedAt
                        ? moment(alert.recordedAt).format(`${TIMELINE_CONST.DATE_FORMAT} ${TIMELINE_CONST.TIME_FORMAT}`)
                        : '—'}
                    </span>
                  </div>
                  <p className="mh-archive-detail">{alert.detail}</p>
                  <div className="mh-archive-meta">
                    <span className={`mh-pill ${scoreMeta.className}`}>{scoreMeta.label}</span>
                    <span className={`mh-archive-severity mh-archive-severity--${severityTone}`}>
                      {alert.severity || 'WARN'}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertArchiveModal;
